// ==========================================================================
// screens/Wfh.tsx — tick the days you worked from home (FF-007, 9 Oct 2026)
// The ATO's fixed-rate method needs a record of actual hours kept during the
// year. One tap per day; hours default from the person (8) and can be nudged.
// ==========================================================================
import { useMemo, useState } from "react";
import { addDays, addWeeks, format, isAfter, startOfWeek } from "date-fns";
import { ChevronLeft, ChevronRight, Check, Minus, Plus } from "lucide-react";
import ChapterHeader from "@/components/ChapterHeader";
import { Card } from "@/components/ui";
import ActivityExplorer from "@/components/ActivityExplorer";
import { tokenColour } from "@/lib/calendar";
import { useEntities, useMe, usePeople } from "@/lib/household";
import { fyOf, fyRange, useTaxRates } from "@/lib/vehicles";
import { useSetWfhHours, useToggleWfh, useWfhDays } from "@/lib/wfh";

const iso = (d: Date) => format(d, "yyyy-MM-dd");

export default function Wfh() {
  const me = useMe(); const people = usePeople(); const entities = useEntities(); const rates = useTaxRates();
  const adults = (people.data ?? []).filter((p) => p.role === "adult");
  const [personId, setPersonId] = useState<string | undefined>();
  const pid = personId ?? me?.id;
  const person = adults.find((p) => p.id === pid);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const fy = fyOf(weekStart); const r = fyRange(fy);
  const week = useWfhDays(pid, iso(days[0]), iso(days[6]));
  const year = useWfhDays(pid, r.from, r.to);
  const toggle = useToggleWfh(); const setHours = useSetWfhHours();
  const employer = entities.data?.find((e) => e.kind === "company") ?? null;
  const rate = rates.data?.find((x) => x.financial_year_id === fy && x.key === "wfh_cents_per_hour")?.value ?? null;
  const today = new Date();

  const totals = useMemo(() => {
    const list = year.data ?? [];
    const hours = list.reduce((s, d) => s + d.hours, 0);
    const byMonth = Array.from({ length: 12 }, (_, i) => {
      const m = new Date(Number(fy.slice(0, 4)), 6 + i, 1);
      const key = format(m, "yyyy-MM");
      return { label: format(m, "MMM"), hours: list.filter((d) => d.work_date.startsWith(key)).reduce((s, d) => s + d.hours, 0) };
    });
    return { days: list.length, hours, byMonth };
  }, [year.data, fy]);

  return (
    <>
      <ChapterHeader chapter="wfh" title="Working from home" subtitle="Tap each day worked from home. The ATO needs the actual record, kept as you go." />

      {adults.length > 1 && (
        <div className="mb-6 flex gap-2">
          {adults.map((p) => (
            <button key={p.id} type="button" onClick={() => setPersonId(p.id)}
              className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm transition ${p.id === pid ? "font-semibold text-white" : "bg-surface ring-1 ring-line"}`} style={p.id === pid ? { background: tokenColour(p.colour) } : undefined}><span className="h-2.5 w-2.5 rounded-full" style={{ background: p.id === pid ? "white" : tokenColour(p.colour) }} />{p.display_name}</button>
          ))}
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-3">
        <div className="space-y-8 lg:col-span-2">
          <Card className="space-y-4">
            <div className="flex items-center justify-between">
              <button type="button" aria-label="Previous week" onClick={() => setWeekStart((w) => addWeeks(w, -1))} className="rounded-md p-2 hover:bg-surface-raised"><ChevronLeft className="h-5 w-5" /></button>
              <span className="font-medium">Week of {format(weekStart, "d MMMM")}</span>
              <button type="button" aria-label="Next week" disabled={isAfter(addWeeks(weekStart, 1), today)} onClick={() => setWeekStart((w) => addWeeks(w, 1))} className="rounded-md p-2 hover:bg-surface-raised disabled:opacity-30"><ChevronRight className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-7 gap-2">
              {days.map((d) => {
                const rec = week.data?.find((w) => w.work_date === iso(d));
                const future = isAfter(d, today);
                const weekend = d.getDay() === 0 || d.getDay() === 6;
                return (
                  <button key={iso(d)} type="button" disabled={future || toggle.isPending || !pid}
                    onClick={() => pid && toggle.mutate({ existing: rec, personId: pid, date: iso(d), hours: person?.wfh_default_hours ?? 8, employerId: employer?.id ?? null })}
                    className={`flex flex-col items-center gap-1 rounded-lg py-4 transition disabled:opacity-30 ${rec ? "text-white shadow-soft" : weekend ? "bg-bg text-ink-muted ring-1 ring-line/70" : "bg-surface ring-1 ring-line hover:ring-primary/40"}`} style={rec ? { background: tokenColour("chart-4") } : undefined}>
                    <span className="text-xs">{format(d, "EEE")}</span>
                    <span className="num text-2xl font-semibold">{format(d, "d")}</span>
                    <span className="h-4">{rec && <Check className="h-4 w-4" />}</span>
                  </button>
                );
              })}
            </div>
            {(week.data?.length ?? 0) > 0 && (
              <ul className="divide-y divide-line border-t border-line">
                {week.data!.map((w) => (
                  <li key={w.id} className="flex items-center justify-between py-2">
                    <span className="text-sm">{format(new Date(w.work_date + "T00:00"), "EEEE d MMM")}</span>
                    <span className="flex items-center gap-2">
                      <button type="button" aria-label="Less" onClick={() => setHours.mutate({ id: w.id, hours: Math.max(0.5, w.hours - 0.5) })} className="rounded-md border border-line p-1"><Minus className="h-4 w-4" /></button>
                      <span className="num w-14 text-center font-semibold">{w.hours} h</span>
                      <button type="button" aria-label="More" onClick={() => setHours.mutate({ id: w.id, hours: Math.min(24, w.hours + 0.5) })} className="rounded-md border border-line p-1"><Plus className="h-4 w-4" /></button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-8">
          <div className="overflow-hidden rounded-lg bg-surface shadow-soft ring-1 ring-line/70">
            <div className="bg-panel px-6 py-5 text-panel-ink">
              <div className="text-sm text-panel-muted">{person?.display_name ?? ""} · {fy}</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="num text-[40px] font-semibold leading-none" style={{ color: "#8FC1E3" }}>{Math.round(totals.hours * 10) / 10}</span>
                <span className="text-sm text-panel-muted">hours over {totals.days} days</span>
              </div>
              <div className="mt-2 text-sm">{rate === null ? <span className="text-panel-muted">Claim estimate waits for the {fy} ATO rate</span> : <>Estimated claim <span className="num font-semibold">${Math.round((totals.hours * rate) / 100).toLocaleString("en-AU")}</span></>}</div>
            </div>
            <div className="p-6">
              <ActivityExplorer key={`${pid}-${fy}`} points={totals.byMonth.map(m=>({label:m.label,value:m.hours}))} kind="hours" loading={year.isPending} error={year.isError}/>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
